/**
 * Opens a blank helper tab: `downloadFile`'s fallback target, and the tab a
 * later `location.replace` navigates to a freshly fetched presigned URL.
 *
 * Must be called synchronously inside the click handler: the click's transient
 * user activation expires after a few seconds, so a `window.open` issued only
 * after an async wait (the blob fetch, a GraphQL round trip) gets popup-blocked
 * and silently does nothing. Returns null when even the synchronous open is
 * blocked — the caller should treat that as "pop-ups are disabled".
 */
export const openDownloadFallbackTab = (title = "Download…"): Window | null => {
    const tab = window.open("about:blank", "_blank");
    if (tab) {
        // Sever reverse access for the page loaded later; our handle keeps working.
        tab.opener = null;
        try {
            tab.document.title = title;
        } catch {
            // cross-origin edge case — the tab just stays untitled
        }
    }
    return tab;
};

/**
 * Triggers a real download of a file in the browser.
 *
 * Fetches the file as a blob and saves it client-side, which forces a download
 * even for cross-origin presigned URLs that lack a `Content-Disposition` header
 * (where the anchor `download` attribute would otherwise be ignored and the
 * browser would just navigate to the file).
 *
 * Falls back to showing the URL in a tab if the fetch is blocked (e.g. CORS).
 * Pass `fallbackTab` (from {@link openDownloadFallbackTab}, opened synchronously
 * in the click handler) when the fetch may outlive the click's user activation —
 * otherwise the fallback `window.open` gets popup-blocked.
 */
export const downloadFile = async (url: string, filename: string, fallbackTab?: Window | null) => {
    try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (fallbackTab && !fallbackTab.closed) fallbackTab.close();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename;
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
    } catch {
        // CORS-blocked fetch etc. → show the file in a tab (don't navigate away)
        if (fallbackTab && !fallbackTab.closed) {
            fallbackTab.location.replace(url);
        } else {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    }
};
